import React, { useState } from "react";
import { EmptyState } from "../../../../components/ui/PageBits";
import { fmtHour, slotCfg } from "../../bookingService";
import { fmtLimit, effectiveTypeLimit, splitShopResources, shopTypes, excessShopTypes } from "../../bookingPlans";
import TypeSettingsForm from "../TypeSettingsForm";

/**
 * Shop calendars: the shop defines its own resource types
 * (Dining Tables, Doctors, Classes — whatever the business needs),
 * names its resources (doctor1, doctor2…), and controls slot length,
 * opening hours, minimum notice and booking window per calendar.
 */
export default function CalendarsTab({
    shop, plans, resourceTypes, resources, bookings,
    onAddType, onUpdateType, onDeleteType, onAddResource, onRenameResource, onDeleteResource,
}) {
    const [tName, setTName] = useState("");
    const [tLabel, setTLabel] = useState("");
    const [tSlot, setTSlot] = useState(30);
    const [resName, setResName] = useState({});
    const [settingsFor, setSettingsFor] = useState(null); // typeId with open settings
    const [renamingRes, setRenamingRes] = useState(null); // resourceId
    const [renameResVal, setRenameResVal] = useState("");

    const myTypes = shopTypes(resourceTypes, shop.id).sort((a, b) =>
        String(a.createdAt).localeCompare(String(b.createdAt))
    );
    const { limit, locked } = splitShopResources(shop, resources, plans);
    const lockedIds = new Set(locked.map((r) => r.id));
    const excessIds = new Set(excessShopTypes(shop, resourceTypes, plans).map((t) => t.id));
    const tLimit = effectiveTypeLimit(shop, plans);

    const activeBookingsFor = (resId) =>
        bookings.filter((b) => {
            if (b.resourceId !== resId) return false;
            if (b.status === "cancelled" || b.status === "noshow") return false;

            const bookingEnd = new Date(`${b.date}T${b.end}`);
            return bookingEnd > new Date();
        }).length;

    return (
        <div className="space-y-4">
            <div className="hk-card">
                <h5 className="hk-card-title">
                    My calendars · {myTypes.length}/{fmtLimit(tLimit)} types ·{" "}
                    {resources.filter((r) => r.ownerId === shop.id).length}/{fmtLimit(limit)} resources
                </h5>
                <div className="flex flex-col md:flex-row gap-2">
                    <div className="md:flex-1">
                        <label>Calendar Name</label>
                        <input className="hk-input" placeholder="e.g. Dining Tables, Doctors, Yoga Classes" value={tName} onChange={(e) => setTName(e.target.value)} />
                    </div>
                    <div className="md:flex-1">
                        <label>Resources</label>
                        <input className="hk-input" placeholder="e.g. Table, Doctor, Mat" value={tLabel} onChange={(e) => setTLabel(e.target.value)} />
                    </div>
                    <div className="md:flex-1">
                        <label>Slot Size (minutes)</label>
                        <input className="hk-input" type="number" min="5" step="5" value={tSlot} onChange={(e) => setTSlot(e.target.value)} title="Slot length (minutes)" />
                    </div>
                    <button
                        className="hk-btn hk-btn-primary shrink-0"
                        onClick={() => {
                            onAddType({ name: tName, resourceLabel: tLabel, slotMin: tSlot });
                            setTName(""); setTLabel(""); setTSlot(30);
                        }}
                    >
                        + Add calendar
                    </button>
                </div>
                <p className="text-xs text-[#64748b] mt-2 mb-0">
                    Each calendar books one kind of resource. Name them however your business works —
                    a clinic uses Doctors, a restaurant uses Tables, a studio uses Classes.
                </p>
            </div>

            {myTypes.length === 0 && (
                <div className="hk-card">
                    <EmptyState icon="🗓️" title="No calendars yet" hint="Add your first calendar above, then add the resources customers can book." />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {myTypes.map((t) => {
                    const mine = resources.filter((r) => r.typeId === t.id && r.ownerId === shop.id);
                    const frozen = excessIds.has(t.id);
                    const cfg = slotCfg(t);
                    const open = settingsFor === t.id;
                    return (
                        <div key={t.id} className="hk-card" style={frozen ? { opacity: 0.8 } : undefined}>
                            <div className="flex justify-between items-start gap-2 mb-1">
                                <div className="min-w-0">
                                    <h5 className="font-bold hk-display text-lg leading-tight">
                                        {t.name} {frozen && <span className="hk-pill hk-pill-amber ml-1">frozen</span>}
                                    </h5>
                                    <div className="text-xs text-[#64748b]">
                                        {t.resourceLabel}s · {cfg.slotMin} min · {fmtHour(cfg.dayStart)}–{fmtHour(cfg.dayEnd % 24)} · {cfg.leadMin}m notice · {cfg.advanceDays}d window
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <button
                                        className="hk-btn hk-btn-outline hk-btn-sm"
                                        onClick={() => setSettingsFor(open ? null : t.id)}
                                    >
                                        {open ? "Close" : "Settings"}
                                    </button>
                                    <button
                                        className="hk-btn hk-btn-outline hk-btn-sm"
                                        style={{ color: "var(--hk-danger)" }}
                                        disabled={mine.length > 0}
                                        title={mine.length > 0 ? "Delete this calendar's resources first" : "Delete calendar"}
                                        onClick={() => {
                                            if (window.confirm(`Delete calendar "${t.name}"?`)) onDeleteType(t.id);
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            {open && (
                                <div className="mt-3">
                                    <TypeSettingsForm
                                        type={t}
                                        onSave={(patch) => {
                                            onUpdateType(t.id, patch);
                                            setSettingsFor(null);
                                        }}
                                    />
                                </div>
                            )}

                            <div className="space-y-2 mt-3">
                                {mine.map((r) => {
                                    const n = activeBookingsFor(r.id);
                                    const isLocked = lockedIds.has(r.id);

                                    if (renamingRes === r.id) {
                                        return (
                                            <div key={r.id} className="flex gap-2">
                                                <input
                                                    className="hk-input"
                                                    value={renameResVal}
                                                    onChange={(e) => setRenameResVal(e.target.value)}
                                                    placeholder={`${t.resourceLabel} name`}
                                                />
                                                <button
                                                    className="hk-btn hk-btn-primary hk-btn-sm shrink-0"
                                                    onClick={() => { onRenameResource(r.id, renameResVal); setRenamingRes(null); }}
                                                >
                                                    Save
                                                </button>
                                                <button className="hk-btn hk-btn-outline hk-btn-sm shrink-0" onClick={() => setRenamingRes(null)}>
                                                    ✕
                                                </button>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={r.id} className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border border-[#e2e8f0]">
                                            <div className="min-w-0">
                                                <span className="font-bold text-sm">{isLocked ? "🔒 " : ""}{r.name}</span>
                                                <span className="text-xs text-[#64748b] ml-2">
                                                    {n} active booking{n === 1 ? "" : "s"}{isLocked ? " · frozen (over limit)" : ""}
                                                </span>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    onClick={() => { setRenamingRes(r.id); setRenameResVal(r.name); }}
                                                >
                                                    Rename
                                                </button>
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    style={{ color: "var(--hk-danger)" }}
                                                    disabled={n > 0}
                                                    title={n > 0 ? "Cancel its bookings first" : "Delete resource"}
                                                    onClick={() => {
                                                        if (window.confirm(`Delete ${r.name}?`)) onDeleteResource(r.id);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {mine.length === 0 && (
                                    <p className="text-xs text-[#64748b]">No {t.resourceLabel.toLowerCase()}s yet — add the first one below.</p>
                                )}
                            </div>

                            <div className="flex gap-2 mt-3">
                                <input
                                    className="hk-input"
                                    placeholder={`New ${t.resourceLabel} name (e.g. ${t.resourceLabel} ${mine.length + 1})`}
                                    value={resName[t.id] || ""}
                                    onChange={(e) => setResName((p) => ({ ...p, [t.id]: e.target.value }))}
                                />
                                <button
                                    className="hk-btn hk-btn-outline hk-btn-sm shrink-0"
                                    onClick={() => {
                                        onAddResource(t.id, resName[t.id]);
                                        setResName((p) => ({ ...p, [t.id]: "" }));
                                    }}
                                >
                                    + Add
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
